import type { AbsolutePath } from "shelving";
import { formatPercent } from "shelving";
import { Icon, MenuButton, Page } from "shared/react";
import { CHALLENGE_POINTS, type ContentEntity } from "shared/schema";
import { pluralizeCompactQuantity } from "shared/util/number";
import { EntityButton, RewardButton } from "../entity/EntityButton.js";
import { ChallengeLeaderboardList } from "../leaderboard/LeaderboardList.js";
import { EmbedMarkup } from "../misc/EmbedMarkup.js";
import { PostGrid } from "../post/PostGrid.js";
import { ReactionSummary } from "../reaction/ReactionSummary.js";
import { RewardIcon } from "../reward/RewardIcon.js";
import { useAttemptItem, useCreatorItem } from "../util/data.js";
import { useItem } from "../util/db.js";
import { useRewardStyle } from "../util/reward.js";
import { ChallengeMenu } from "./ChallengeMenu.js";
import { ChallengeButtons, ChallengeStatus, ChallengeTasks } from "./ChallengeTasks.js";

export function ChallengePage({ id }: { id: string }): React.ReactElement {
	const challenge = useItem("challenge", id);
	const attempt = useAttemptItem(id);
	const { slug, creator, name, about, badge, background, reaction, reactions, started, completed } = challenge.data;
	const username = useCreatorItem(creator).value?.username;
	const entity: ContentEntity = `challenge:${id}`;
	const path: AbsolutePath = username ? `/${username}/${slug}` : `/challenge/${id}`;
	return (
		<Page title={name} path={path}>
			<header className="panel pad-3x" style={useRewardStyle(background)}>
				<div className="flex-row gap-xsmall width-fit">
					<RewardIcon id={badge} type="badge" mods="size-4x" />
					<h1 className="text-left">
						<small className="label">Challenge</small>
						<span>{name}</span>
					</h1>
				</div>
				<MenuButton menu={<ChallengeMenu id={id} />} mods="button-quiet pin-top pin-right" />
				<EntityButton entity={creator} mods="pin-bottom pin-left size-small" />
				<ReactionSummary target={entity} count={reactions} mods="pin-bottom pin-right size-small" />
				<ul className="status">
					<li>{pluralizeCompactQuantity(started, "person attempting", "people attempting")}</li>
					<li>
						{pluralizeCompactQuantity(completed, "person completed", "people completed")}
						{started ? ` (${formatPercent(completed, started)})` : ""}
					</li>
				</ul>
			</header>
			<section>
				<ChallengeTasks id={id} challenge={challenge.data} attempt={attempt.value} />
				<footer>
					<ChallengeButtons id={id} challenge={challenge.data} attempt={attempt.value} mods="space-xsmall" />
					<ChallengeStatus challenge={challenge.data} attempt={attempt.value} mods="space-xsmall" />
				</footer>
			</section>
			<section>
				<h2>About</h2>
				<EmbedMarkup>{about}</EmbedMarkup>
				<h2 className="label">Rewards</h2>
				<p className="flex-row flex-center flex-wrap gap-xsmall size-small">
					<a className="button fill-gold color-black" href="/help/points">
						<Icon icon="star" />
						<span>{`${CHALLENGE_POINTS} points`}</span>
					</a>
					{badge && <RewardButton id={badge} />}
					{background && <RewardButton id={background} />}
					{reaction && <RewardButton id={reaction} />}
				</p>
			</section>
			<hr />
			<section>
				<h2>Leaderboards</h2>
				<ChallengeLeaderboardList cid={id} empty="No leaderboards joined" />
			</section>
			<hr />
			<section>
				<h2>Victories</h2>
				<PostGrid //
					query={{ "tags[]": entity, "privacy": "public" }}
					empty="No victory posts yet"
				/>
			</section>
		</Page>
	);
}
